<template>
  <div>
    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <button 
        class="mr-2 p-3 text-white bg-teal-800 hover:bg-teal-900 rounded-lg transition-colors" 
        @click="back()"
        title="Volver atrás"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-3xl font-bold">Nueva Compra</h1>
    </div>

    <Head title="Nueva Compra" />

    <!-- Información de la compra -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold mb-4">Información de la Compra</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Selector de Proveedor -->
          <select-input
            v-model="form.provider_id"
            :error="form.errors.provider_id"
            label="Proveedor *"
            class="w-full"
          >
            <option value="">Seleccionar proveedor</option>
            <option 
              v-for="provider in providers" 
              :key="provider.id" 
              :value="provider.id"
            >
              {{ provider.name }} - {{ provider.company_name }}
            </option>
          </select-input>

          <!-- Número de Factura -->
          <text-input
            v-model="form.invoice_number"
            :error="form.errors.invoice_number"
            label="Número de Factura *"
            placeholder="Ingrese el número de factura..."
          />

          <!-- Tipo de Pago -->
          <select-input
            v-model="form.payment_type"
            :error="form.errors.payment_type"
            label="Tipo de Pago *"
            class="w-full"
          >
            <option value="cash">Contado</option>
            <option value="credit">Crédito</option>
          </select-input>

          <!-- Depósito -->
          <!-- <select-input
            v-model="form.deposit_id"
            :error="form.errors.deposit_id"
            label="Depósito *"
            class="w-full"
          >
            <option value="">Seleccionar depósito</option>
            <option 
              v-for="deposit in deposits" 
              :key="deposit.id" 
              :value="deposit.id"
            >
              {{ deposit.name }}
            </option>
          </select-input> -->

          <!-- Fecha del documento -->
          <text-input
            v-model="form.date"
            :error="form.errors.date"
            type="date"
            label="Fecha del Documento"
            class="w-full"
          />

          <!-- Fecha de Vencimiento (solo para crédito) -->
          <text-input
            v-if="form.payment_type === 'credit'"
            v-model="form.due_date"
            :error="form.errors.due_date"
            type="date"
            label="Fecha de Vencimiento *"
            class="w-full"
          />
        </div>
      </div>
    </div>

    <!-- Buscador de productos -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold">Búsqueda de Productos</h3>
        
        <div class="mt-4">
          <text-input 
            v-model="form.search.terms"
            label="Buscar por nombre, código o descripción"
            placeholder="Ingrese términos de búsqueda"
            class="w-full lg:w-1/2"
          />
        </div>
      </div>

      <!-- Tabla de resultados de búsqueda -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50">
              <th class="py-3 px-6 font-semibold">Código</th>
              <th class="py-3 px-6 font-semibold">Producto</th>
              <th class="py-3 px-6 font-semibold">Costo</th>
              <th class="py-3 px-6 font-semibold">% Ganancia</th>
              <th class="py-3 px-6 font-semibold">Precio Venta</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="product in products" 
              :key="product.id"
              class="hover:bg-gray-50 cursor-pointer transition-colors"
              @click="selectProduct(product)"
            >
              <td class="py-3 px-6">{{ product?.code }}</td>
              <td class="py-3 px-6">{{ product?.name }}</td>
              <td class="py-3 px-6">{{ formatCurrency(product?.cost) }}</td>
              <td class="py-3 px-6">{{ product?.percentage || 0 }}%</td>
              <td class="py-3 px-6">{{ formatCurrency(sellingPrices(product)) }}</td>
            </tr>
            <tr v-if="!products?.length">
              <td colspan="6" class="py-4 px-6 text-center text-gray-500">
                No se encontraron productos
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

        <!-- Lista de productos seleccionados -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold">Productos a Comprar</h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="p-3 text-left">Código</th>
              <th class="p-3 text-left">Producto</th>
              <th class="p-3 text-center">Cantidad</th>
              <th class="p-3 text-center">Costo</th>
              <th class="p-3 text-center">(%) Ganancia</th>
              <th class="p-3 text-right">Precio Venta</th>
              <th class="p-3 text-right">Total</th>
              <th class="p-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in form.products" :key="product.id" class="border-t">
              <td class="p-3">{{ product.code }}</td>
              <td class="p-3">{{ product.name }}</td>
              <td class="p-3">
                <text-input
                  v-model.number="form.products[index].quantity"
                  type="number"
                  class="w-20 text-center"
                />
              </td>
              <td class="p-3">
                <text-input
                  v-model.number="form.products[index].cost"
                  type="number"
                  class="w-24 text-right"
                />
              </td>
              <td class="p-3">
                <text-input
                  v-model.number="form.products[index].percentage"
                  type="number"
                  class="w-24 text-right"
                />
              </td>
              <td class="p-3 text-right">
                {{ formatCurrency(sellingPrices(product)) }}
              </td>
              <td class="p-3 text-right">
                ${{ (form.products[index].cost * product.quantity).toFixed(2) }}
              </td>
              <td class="p-3 text-center">
                <button
                  v-if="product.has_imei"
                  @click.prevent="openImeiModal(index)"
                  class="p-2 mr-2 text-white bg-indigo-500 hover:bg-indigo-600 rounded-full transition-colors relative"
                  title="Ingresar IMEIs"
                >
                  <i class="fa fa-barcode"></i>
                  <span v-if="product.imeis && product.imeis.length > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {{ product.imeis.length }}
                  </span>
                </button>
                <button
                  @click="removeProduct(product)"
                  class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                  title="Eliminar producto"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
          
          <tfoot class="bg-gray-50">
            <tr>
              <td colspan="2" class="p-3">
                <select-input
                  v-model="form.deposit_id"
                  :error="form.errors.deposit_id"
                  label="Depósito destino *"
                  class="w-full"
                >
                  <option value="">Seleccione un depósito</option>
                  <option v-for="deposit in deposits" :key="deposit.id" :value="deposit.id">
                    {{ deposit.name }}
                  </option>
                </select-input>
              </td>
              <td colspan="3" class="p-3 text-right">Subtotal:</td>
              <td colspan="2" class="p-3 text-right">{{ formatCurrency(getSubtotal()) }}</td>
              <td></td>
            </tr>
            <tr>
              <td colspan="5" class="p-3 text-right">Descuento ({{ form.discount_percentage }}%):</td>
              <td colspan="2" class="p-3 text-right text-red-600">-{{ formatCurrency(form.discount_amount) }}</td>
              <td></td>
            </tr>
            <tr>
              <td colspan="5" class="p-3 text-right">Total:</td>
              <td colspan="2" class="p-3 text-right">
                <div class="text-xl font-bold">{{ formatCurrency(getTotal()) }}</div>
              </td>
              <td></td>
            </tr>
            <tr v-if="form.payment_type === 'credit'">
              <td colspan="5" class="p-3 text-right">Total Pagado:</td>
              <td colspan="2" class="p-3 text-right">{{ formatCurrency(getTotalPaid()) }}</td>
              <td></td>
            </tr>
            <tr v-if="form.payment_type === 'credit'">
              <td colspan="5" class="p-3 text-right">Balance Pendiente:</td>
              <td colspan="2" class="p-3 text-right text-red-600">{{ formatCurrency(getRemainingBalance()) }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Descuento -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold mb-4">Descuento</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <text-input
            v-model="form.discount_percentage"
            type="number"
            label="Porcentaje de Descuento (%)"
            class="w-full"
            :min="0"
            :max="100"
            placeholder="Ingrese el porcentaje"
            @change="calculateDiscount"
          />
          <text-input
            v-model="form.discount_amount"
            type="number"
            label="Monto de Descuento ($)"
            class="w-full"
            :min="0"
            :max="getSubtotal()"
            placeholder="Ingrese el monto"
            @change="calculateDiscountPercentage"
          />
        </div>
      </div>
    </div>

    <!-- Pagos para cualquier tipo -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold mb-4">Método de Pago</h3>
        <shopping-payment-box
          :payment_methods="payment_methods"
          :balance="form.total ? parseFloat(form.total) - getTotalPaid() : 0"
          :change="current_exchange_rate"
          @add-payment="addPayment"
        />
      </div>
      
      <!-- Tabla de pagos agregados -->
      <div v-if="form.payments && form.payments.length > 0" class="p-6">
        <h4 class="text-lg font-semibold mb-3">Pagos Agregados</h4>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="bg-gray-50">
                <th class="py-3 px-4 text-left font-semibold">Método</th>
                <th class="py-3 px-4 text-right font-semibold">Monto USD</th>
                <th class="py-3 px-4 text-right font-semibold">Monto Bs</th>
                <th class="py-3 px-4 text-right font-semibold">Tasa</th>
                <th class="py-3 px-4 text-left font-semibold">Referencia</th>
                <th class="py-3 px-4 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(payment, index) in form.payments" :key="index" class="border-t">
                <td class="py-3 px-4">{{ getPaymentMethodName(payment.method) }}</td>
                <td class="py-3 px-4 text-right">{{ formatCurrency(payment.amount) }}</td>
                <td class="py-3 px-4 text-right">{{ payment.amount_bs ? formatCurrency(payment.amount_bs, 'VES') : '-' }}</td>
                <td class="py-3 px-4 text-right">{{ payment.exchange_rate ? payment.exchange_rate : '-' }}</td>
                <td class="py-3 px-4">{{ payment.reference || '-' }}</td>
                <td class="py-3 px-4 text-center">
                  <button 
                    @click="removePayment(index)" 
                    class="p-1 text-red-500 hover:text-red-700"
                    title="Eliminar pago"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="bg-gray-50 font-semibold">
                <td class="py-3 px-4 text-right" colspan="1">Total Pagado:</td>
                <td class="py-3 px-4 text-right">{{ formatCurrency(getTotalPaid()) }}</td>
                <td colspan="4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div class="p-6 bg-gray-50">
        <button 
          @click="generate"
          class="btn-blue text-lg px-8 py-3"
          :disabled="!form.products.length"
        >
          <i class="fa fa-check mr-2"></i>
          Generar Compra
        </button>
      </div>
    </div>

    <!-- Modal IMEI -->
    <div v-if="imeiModal.show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        class="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden my-auto"
        style="max-height: 85vh;"
      >
        <div class="px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h3 class="text-lg font-bold flex items-center gap-2">
            <i class="fa fa-barcode text-indigo-600"></i>
            <span>Ingresar IMEIs</span>
          </h3>
          <button @click="closeImeiModal" class="text-gray-400 hover:text-gray-600">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div 
          class="p-6 flex-1 overflow-y-auto"
          style="max-height: calc(85vh - 130px);"
        >
          <div class="mb-4 p-3 bg-blue-50 text-blue-800 rounded-lg text-xs leading-relaxed">
            <p class="font-semibold mb-1">Producto: {{ form.products[imeiModal.productIndex]?.name }}</p>
            <p>Cantidad requerida: <strong>{{ form.products[imeiModal.productIndex]?.quantity }}</strong> IMEIs (exactamente 15 dígitos numéricos por IMEI).</p>
          </div>

          <div v-for="i in form.products[imeiModal.productIndex]?.quantity" :key="i" class="mb-3">
            <div class="flex justify-between items-center mb-1">
              <label :for="`shopping-imei-${i}`" class="text-xs font-semibold text-gray-700">
                IMEI {{ i }} *
              </label>
              <span v-if="imeiModal.imeis[i-1] && !/^\d{15}$/.test(imeiModal.imeis[i-1].trim())" class="text-xs text-red-500 font-medium">
                {{ imeiModal.imeis[i-1].trim().length }} / 15 dígitos
              </span>
              <span v-else-if="imeiModal.imeis[i-1] && /^\d{15}$/.test(imeiModal.imeis[i-1].trim())" class="text-xs text-green-600 font-semibold flex items-center gap-1">
                <i class="fa fa-check"></i> Válido (15 dígitos)
              </span>
            </div>
            <input
              :id="`shopping-imei-${i}`"
              v-model="imeiModal.imeis[i-1]"
              type="text"
              maxlength="15"
              placeholder="Escanee o escriba el IMEI de 15 dígitos"
              class="w-full border rounded-lg p-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 transition-colors"
              :class="{
                'border-red-400 bg-red-50 text-red-900': imeiModal.imeis[i-1] && !/^\d{15}$/.test(imeiModal.imeis[i-1].trim()),
                'border-green-500 bg-green-50 text-green-900': imeiModal.imeis[i-1] && /^\d{15}$/.test(imeiModal.imeis[i-1].trim()),
                'border-gray-300': !imeiModal.imeis[i-1]
              }"
              @keydown.enter.prevent="handleImeiEnter(i)"
            />
          </div>
        </div>
        <div class="px-6 py-4 bg-gray-50 border-t flex justify-end gap-2 shrink-0">
          <button @click="closeImeiModal" class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <button @click="saveImeis" class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
            Guardar IMEIs
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import ShoppingPaymentBox from '@/Shared/ShoppingPaymentBox'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    ShoppingPaymentBox,
  },
  
  layout: Layout,
  
  props: {
    products: Array,
    deposits: Array,
    providers: Array,
    payment_methods: Array,
    current_exchange_rate: Number,
  },

  data() {
    return {
      form: this.$inertia.form({
        provider_id: '',
        date: new Date().toISOString().slice(0, 10),
        invoice_number: '',
        payment_type: 'cash',
        due_date: '',
        deposit_id: null,
        payment_method_id: '',
        discount_percentage: '',
        discount_amount: '',
        total: '',
        sub_total: '',
        payments: [],
        search: {
          terms: ''
        },
        products: []
      }),
      imeiModal: {
        show: false,
        productIndex: null,
        imeis: []
      },
      exchange_rate: this.current_exchange_rate || 0,
      selectedPaymentMethod: null,
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    openImeiModal(index) {
      const product = this.form.products[index];
      this.imeiModal.productIndex = index;
      
      // Initialize with existing imeis if they exist, or empty strings
      let existingImeis = product.imeis || [];
      this.imeiModal.imeis = Array.from({ length: product.quantity }, (_, i) => existingImeis[i] || '');
      
      this.imeiModal.show = true;
      this.$nextTick(() => {
        const firstInput = document.getElementById('shopping-imei-1');
        if (firstInput) firstInput.focus();
      });
    },

    closeImeiModal() {
      this.imeiModal.show = false;
      this.imeiModal.productIndex = null;
      this.imeiModal.imeis = [];
    },

    handleImeiEnter(index) {
      const currentVal = (this.imeiModal.imeis[index - 1] || '').trim();
      if (currentVal && !/^\d{15}$/.test(currentVal)) {
        this.$swal.fire({
          icon: 'warning',
          title: 'IMEI inválido',
          text: `El código escaneado "${currentVal}" no tiene 15 dígitos numéricos (tiene ${currentVal.length} caracteres).`,
          confirmButtonText: 'Corregir',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      const total = this.form.products[this.imeiModal.productIndex]?.quantity || 0;
      if (index < total) {
        this.$nextTick(() => {
          const nextInput = document.getElementById(`shopping-imei-${index + 1}`);
          if (nextInput) nextInput.focus();
        });
      } else {
        this.saveImeis();
      }
    },

    saveImeis() {
      // Validar que no haya vacíos
      const filledImeis = this.imeiModal.imeis.map(i => (i || '').trim()).filter(i => i !== '');
      if (filledImeis.length !== this.form.products[this.imeiModal.productIndex].quantity) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Debe ingresar todos los IMEIs correspondientes a la cantidad del producto.',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      // Validar que todos tengan exactamente 15 dígitos numéricos
      const invalidImeis = filledImeis.filter(i => !/^\d{15}$/.test(i));
      if (invalidImeis.length > 0) {
        this.$swal.fire({
          icon: 'error',
          title: 'IMEI inválido',
          text: `Los siguientes códigos no son válidos: ${invalidImeis.join(', ')}. Los IMEIs deben tener exactamente 15 dígitos numéricos.`,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }
      
      // Validar duplicados en el mismo modal
      const uniqueImeis = new Set(filledImeis);
      if (uniqueImeis.size !== filledImeis.length) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Hay IMEIs duplicados en la lista.',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      this.form.products[this.imeiModal.productIndex].imeis = filledImeis;
      this.closeImeiModal();
    },

    addPayment(payment) {
      const total = this.getTotal();
      const currentTotalPaid = this.getTotalPaid();
      const newAmount = parseFloat(payment.amount) || 0;
      const totalPaid = currentTotalPaid + newAmount;

      // Validaciones específicas para pago de contado
      if (this.form.payment_type === 'cash') {
        if (newAmount < (total - currentTotalPaid)) {
          this.$swal.fire({
            icon: 'error',
            title: 'Pago incompleto',
            text: 'El pago de contado debe ser completo',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#3085d6'
          });
          return;
        }
        if (newAmount > (total - currentTotalPaid)) {
          this.$swal.fire({
            icon: 'error',
            title: 'Monto excedido',
            text: 'El pago no debe exceder el monto a pagar',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#3085d6'
          });
          return;
        }
      } else {
        // Para pagos a crédito, solo validar que no exceda el total
        if (totalPaid > total) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Monto excedido',
            text: 'El monto del pago excede el total de la compra',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#3085d6'
          });
          return;
        }
      }

      // Registrar los datos del pago
      const newPayment = {
        method: payment.method,
        amount: parseFloat(payment.amount) || '',
        amount_bs: parseFloat(payment.amount_bs) || '',
        reference: payment.reference || '',
        date: this.form.date, // Usar la fecha actual para todos los pagos
        status: this.form.payment_type === 'cash' ? 'paid' : 'pending' // Marcar como pagado si es de contado
      };
      
      // Agregar el nuevo pago al array
      this.form.payments.push(newPayment);
      
      // Limpiar el formulario de pago
      if (this.$refs.paymentBox) {
        this.$refs.paymentBox.resetForm();
      }
    },

    removePayment(index) {
      this.form.payments.splice(index, 1)
    },

    calculateDiscount() {
      if (this.form.discount_percentage === '') {
        this.form.discount_amount = ''
        return
      }

      const percentage = parseFloat(this.form.discount_percentage)
      if (percentage >= 0 && percentage <= 100) {
        const subtotal = this.getSubtotal()
        // Calcular el monto del descuento
        const amount = (subtotal * percentage) / 100
        this.form.discount_amount = amount.toFixed(2)
      } else {
        this.form.discount_amount = ''
      }
    },

    calculateDiscountPercentage() {
      if (this.form.discount_amount === '') {
        this.form.discount_percentage = ''
        return
      }

      const subtotal = this.getSubtotal()
      if (subtotal > 0) {
        const amount = parseFloat(this.form.discount_amount)
        if (amount <= subtotal) {
          const percentage = (amount / subtotal) * 100
          this.form.discount_percentage = percentage.toFixed(2)
        } else {
          // Si el monto es mayor al subtotal, establecer el descuento al 100%
          this.form.discount_amount = subtotal.toFixed(2)
          this.form.discount_percentage = '100'
        }
      } else {
        this.form.discount_percentage = ''
      }
    },

    searchProducts() {
      this.$inertia.get('/shoppings/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true
        }
      )
    },

    selectProduct(product) {
      const exists = this.form.products.some(p => p.id === product.id)
      
      if (!exists) {
        this.form.products.push({
          ...product,
          quantity: 1,
          cost: product.cost || 0,
          price: 0,
          percentage: 0,
          imeis: []
        })
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.form.products = this.form.products.filter(p => p.id !== product.id)
      }
    },

    getSubtotal() {
      return this.form.products ? this.form.products.reduce((sum, product) => sum + (product.quantity * product.cost), 0) : 0
    },

    getTotal() {
      const subtotal = parseFloat(this.getSubtotal()) || 0
      const discount = this.form.discount_amount === '' ? 0 : parseFloat(this.form.discount_amount) || 0
      const validDiscount = Math.min(discount, subtotal)
      
      // Asegurar que el total sea un número válido
      const total = subtotal - validDiscount
      return !isNaN(total) && total >= 0 ? total : ''
    },

    getTotalPaid() {
      return this.form.payments ? this.form.payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0) : 0
    },

    getRemainingBalance() {
      const total = parseFloat(this.getTotal())
      const paidAmount = parseFloat(this.getTotalPaid())
      return Math.max(0, total - paidAmount).toFixed(2)
    },

    formatCurrency(value, currency = 'USD') {
      return new Intl.NumberFormat('es-US', {
        style: 'currency',
        currency: currency
      }).format(value || 0)
    },

    generate() {
      // Validaciones básicas
      if (!this.form.provider_id) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Debe seleccionar un proveedor',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      if (!this.form.invoice_number) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Debe ingresar el número de factura',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Verificar que haya productos en la compra
      if (!this.form.products || this.form.products.length === 0) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Debe agregar al menos un producto',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        })
        return
      }
      
      // Calcular el total con validación robusta
      const total = this.getTotal();
      if (isNaN(total) || total <= 0) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'El total de la compra debe ser mayor a cero',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        })
        return;
      }

      // Para pagos de contado, validar que el pago sea completo
      if (this.form.payment_type === 'cash') {
        const totalPaid = this.getTotalPaid()
        if (totalPaid !== total) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'El pago de contado debe ser completo',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#3085d6'
          })
          return
        }
      }
      
      // Asignar valores finales
      this.form.total = parseFloat(total).toFixed(2);
      this.form.sub_total = parseFloat(this.getSubtotal()).toFixed(2);
      this.form.status = this.form.payment_type === 'cash' ? 'paid' : 'pending';
      
      // Si es pago de contado, la fecha de vencimiento es igual a la fecha actual
      if (this.form.payment_type === 'cash' && !this.form.due_date) {
        this.form.due_date = this.form.date;
      }
      
      // Validar que haya al menos un pago registrado
      if (this.checkIncompletePayments()) {
        alert('Debe agregar al menos un método de pago')
        return;
      }
      
      // Validar que el deposito sea válido
      if (!this.form.deposit_id) {
        alert('Debe seleccionar un depósito');
        return;
      }
      
      // Asegurarnos de que deposit_id sea un número válido
      this.form.deposit_id = parseInt(this.form.deposit_id) || null;
      
      // console.log('Datos a enviar:', this.form);
      
      this.form.post('/shoppings')
    },

    checkIncompletePayments() {
      return !this.form.payments && this.form.payment_type == 'cash' || this.form.payments.length === 0 && this.form.payment_type == 'cash'
    },

    getPaymentMethodName(methodId) {
      const method = this.payment_methods.find(m => m.id === methodId)
      return method ? method.name : 'Desconocido'
    },
  },

  mounted() {
    // Asegurar que discount_percentage y discount_amount sean números
    this.form.discount_percentage = parseFloat(this.form.discount_percentage) || 0
    this.form.discount_amount = parseFloat(this.form.discount_amount) || 0
  },

  computed: {
      sellingPrices() {
        return (product) => {
          const cost = parseFloat(product?.cost) || 0;
          const percentage = parseFloat(product?.percentage) || 0;
          const profit = (cost * percentage) / 100;
          product.price = cost + profit;
          return cost + profit;
        }
      }
    },

  watch: {
    'form.search': {
      handler: debounce(function() {
        this.searchProducts()
      }, 300),
      deep: true
    },
    'form.products': {
      handler() {
        // Cuando los productos cambian, actualizar el monto de descuento
        if (this.form.discount_percentage > 0) {
          this.calculateDiscount()
        }
      },
      deep: true
    }
  }
}
</script>